/**
 * Last 5 ledger movements for the client dashboard.
 *
 * Shows category + direction with a coloured badge, amount, and
 * relative timestamp. Tapping "View all" navigates to /client/portfolio
 * for the full paginated history.
 */
import { Link } from 'react-router-dom';
import { formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import type { SummaryMovement } from '../../types/finance';
import { readerLocale } from '../../i18n/language';


interface Props {
  movements: SummaryMovement[];
}

const CATEGORY_LABEL: Record<string, string> = {
  deposit:       'Contribution',
  yield_accrual: 'Result',
  withdrawal:    'Withdrawal',
  fee:           'Management fee',
  reversal:      'Reversal',
  adjustment:    'Adjustment',
  nav_revaluation: 'Change in value',
};

function categoryColor(category: string, entryType: string): { bg: string; text: string } {
  if (category === 'deposit')       return { bg: colors.successBg, text: colors.successText };
  if (category === 'yield_accrual' || category === 'nav_revaluation') return { bg: '#EFF6FF', text: '#1D4ED8' };
  if (category === 'withdrawal')    return { bg: colors.dangerBg, text: colors.danger };
  if (category === 'reversal')      return { bg: colors.warningBg, text: colors.warning };
  if (entryType === 'credit')       return { bg: colors.successBg, text: colors.successText };
  return { bg: colors.canvas, text: colors.inkSoft };
}

function relativeTime(iso: string): string {
  const diff = Date.now() - Date.parse(iso);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(readerLocale(), { month: 'short', day: 'numeric' });
}

export default function RecentActivity({ movements }: Readonly<Props>): JSX.Element {
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: '20px 22px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: colors.muted,
          }}
        >
          Recent activity
        </p>
        <Link
          to="/client/portfolio"
          style={{ fontSize: 12, color: colors.gold, textDecoration: 'none', fontWeight: 600 }}
        >
          View all →
        </Link>
      </div>

      {movements.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: colors.muted }}>No activity yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {movements.map((mv) => {
            const badge = categoryColor(mv.category, mv.entry_type);
            const sign = mv.entry_type === 'credit' ? '+' : '−';
            const amountColor = mv.entry_type === 'credit' ? colors.success : colors.danger;

            return (
              <div
                key={mv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: `1px solid ${colors.borderSoft}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: badge.bg,
                      color: badge.text,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {CATEGORY_LABEL[mv.category] ?? mv.category}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: colors.inkSoft,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mv.product_name ?? '—'}
                  </span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: amountColor }}>
                    {sign}{formatMoney(mv.amount, mv.currency_code)}
                  </p>
                  <p style={{ margin: '1px 0 0 0', fontSize: 11, color: colors.mutedSoft }}>
                    {relativeTime(mv.created_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
